import CheckIcon from "@mui/icons-material/Check";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import FolderIcon from "@mui/icons-material/Folder";
import LaunchIcon from "@mui/icons-material/Launch";
import SaveIcon from "@mui/icons-material/Save";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  SpeedDial,
  SpeedDialAction,
  SpeedDialIcon,
} from "@mui/material";
import axios from "axios";
import Cookies from "js-cookie";
import Quill from "quill";
import React, { useCallback, useEffect, useState } from "react"; // React をインポート、useRef を削除
import { useTranslation } from "react-i18next";
import { useAlert } from "../../../../context/AlertContext";
import { useFileContext } from "../../../../context/FileContext";
import { useCustomNavigate } from "../../../../hooks/useCustomNavigate";

interface TextActionsProps {
  quill: Quill | null;
  fileUuid: string | undefined;
  fileTitle: string | undefined;
  thumbnailImage: string | undefined;
  savedPublicStatus: number;
  setSavedPublicStatus: (status: number) => void;
  boxRef: React.RefObject<HTMLDivElement>;
}

const TextActionsComponent = ({
  quill,
  fileUuid,
  fileTitle,
  thumbnailImage,
  savedPublicStatus,
  setSavedPublicStatus,
  boxRef,
}: TextActionsProps) => {
  // 閉じ括弧を削除し、アローを追加
  const { t } = useTranslation();
  const { setAlert: setGlobalAlert } = useAlert();
  const {
    publicStatus: publicStatusMap,
    tags: tagsMap,
    language: languageMap,
    updateThumbnail,
    saveTrigger,
    publicUuid: publicUuidMap,
    setFileText: contextSetFileText,
    setUnsplash: contextSetUnsplash,
    setGeneratedImage: contextSetGeneratedImage,
    setThumbnailImage: contextSetThumbnailImage,
    setUpdateThumbnail: contextSetUpdateThumbnail,
    setSaveTrigger: contextSetSaveTrigger,
  } = useFileContext();
  const navigate = useCustomNavigate();

  const publicStatusValue = fileUuid ? publicStatusMap[fileUuid] : 0;
  const tagsValue = fileUuid ? tagsMap[fileUuid] : [];
  const languageValue = fileUuid ? languageMap[fileUuid] : "en";
  const currentPublicUuid = fileUuid ? publicUuidMap[fileUuid] : undefined;

  const [saving, setSaving] = useState(false);
  const [saveIcon, setSaveIcon] = useState(<SaveIcon />);
  const [deleting, setDeleting] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [speedDialStyle, setSpeedDialStyle] = useState({});

  // hasTempImage は TextEditor.tsx 側で管理するため削除
  // const hasTempImage = useCallback(() => {
  //   if (!quill) return false;
  //   const html = quill.root.innerHTML;
  //   const tempImageRegex = /<img src="data:image/g;
  //   return tempImageRegex.test(html);
  // }, [quill]);

  const hasImage = useCallback(() => {
    if (!quill) return false;
    const html = quill.root.innerHTML;
    const imageRegex = /<img/g;
    return imageRegex.test(html);
  }, [quill]);

  // 保存処理
  const handleSave = useCallback(async () => {
    if (!quill) return;
    const isNewFile = fileUuid === "new" || fileUuid === undefined;

    try {
      if (!isNewFile && publicStatusValue === 1 && (!thumbnailImage || thumbnailImage.trim() === "")) {
        setGlobalAlert("error", t("textEditor.sidebar.error.publicThumbnail"));
        return;
      }
      if (!isNewFile && publicStatusValue === 1 && !hasImage()) {
        setGlobalAlert("error", t("textEditor.sidebar.error.publicThumbnail"));
        return;
      }

      setSaving(true);
      setSaveIcon(<CircularProgress size={20} />);
      const csrftoken = Cookies.get("csrftoken");
      const headers = { "X-CSRFToken": csrftoken! };
      let currentFileUuid = fileUuid;

      if (isNewFile) {
        // 新規作成の場合
        const newData = {
          title: fileTitle || "NEW FILE", // 新規作成時はデフォルトタイトル
          content: quill.root.innerHTML,
          publicStatus: 2, // デフォルトはプライベート
          language: languageValue,
          type: 1, // Notes Nebulaを示すタイプ (仮)
        };
        const postRes = await axios.post("/api/v1/text-file/", newData, { headers });
        if (postRes.data && postRes.data.uuid) {
          currentFileUuid = postRes.data.uuid;
          navigate(`/notes-nebula/files/${currentFileUuid}`, { replace: true });
          // コンテキストの情報を新しいUUIDで更新する必要があるかもしれない
          // (FileContextがUUIDをキーにしているため、新しいエントリを作成または更新)
          // この部分はFileContextの実装に依存する
          setGlobalAlert("success", "New file created and saved!");
        } else {
          setSaving(false);
          setSaveIcon(<SaveIcon />);
          setGlobalAlert("error", "Failed to create new file.");
          return;
        }
      }

      // 既存ファイル保存 または 新規作成後の初回保存
      if (!currentFileUuid) {
        // currentFileUuidがまだundefinedの場合（POSTリクエスト失敗など）
        setSaving(false);
        setSaveIcon(<SaveIcon />);
        setGlobalAlert("error", "File UUID is missing.");
        return;
      }

      contextSetFileText({ uuid: currentFileUuid!, content: quill.getText() || "" });
      contextSetUnsplash({ uuid: currentFileUuid!, url: "" });
      contextSetGeneratedImage({ uuid: currentFileUuid!, url: "" });
      setSavedPublicStatus(publicStatusValue);

      const patchData = {
        content: quill.root.innerHTML,
        title: fileTitle,
        publicStatus: publicStatusValue,
        thumbnail: thumbnailImage,
        tags: tagsValue,
        language: languageValue,
        updateThumbnail: updateThumbnail,
      };

      await axios
        .patch(`/api/v1/text-file/${currentFileUuid}`, patchData, { headers: headers })
        .then((res) => {
          if (res.data) {
            setSaveIcon(<CheckIcon />);
            setTimeout(() => setSaveIcon(<SaveIcon />), 3000);
            quill.root.innerHTML = res.data.content; // サーバーからの最新のcontentでエディタを更新
            contextSetFileText({ uuid: currentFileUuid!, content: quill.getText() });
            contextSetThumbnailImage({ uuid: currentFileUuid!, url: res.data.thumbnail });
            contextSetUpdateThumbnail(false);
            if (!isNewFile) {
              // 新規作成時は作成成功のメッセージを優先
              setGlobalAlert("success", "Saved successfully");
            }
          }
        })
        .catch((err) => {
          setGlobalAlert("error", "An error occurred during the save operation");
          console.error(err);
        });
    } catch (error) {
      console.error("An unknown error occurred:", error);
      setGlobalAlert("error", "An unexpected error occurred.");
    } finally {
      setSaving(false);
      // isNewFileの場合、SaveIconはCheckIconのままにしない（navigate後の再レンダリングでリセットされる想定）
      if (!isNewFile) {
        setTimeout(() => setSaveIcon(<SaveIcon />), 3000);
      } else {
        setSaveIcon(<SaveIcon />); // 新規作成時は即座にデフォルトアイコンに戻す
      }
    }
  }, [
    quill,
    fileUuid,
    publicStatusValue,
    thumbnailImage,
    hasImage,
    fileTitle,
    tagsValue,
    languageValue,
    updateThumbnail,
    setGlobalAlert,
    t,
    contextSetFileText,
    contextSetUnsplash,
    contextSetGeneratedImage,
    setSavedPublicStatus,
    contextSetThumbnailImage,
    contextSetUpdateThumbnail,
    navigate,
  ]);

  // 保存トリガー
  useEffect(() => {
    if (saveTrigger) {
      handleSave();
      contextSetSaveTrigger(false);
    }
  }, [saveTrigger, handleSave, contextSetSaveTrigger]);

  // ダウンロード処理
  const handleDownload = useCallback(() => {
    if (!quill) return;
    const text = quill.getText() || "";
    const element = document.createElement("a");
    const file = new Blob([text], { type: "text/plain" });
    element.href = URL.createObjectURL(file);
    element.download = fileTitle || "file";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  }, [quill, fileTitle]);

  // 削除確認ダイアログを開く
  const handleDeleteDialogOpen = () => {
    setDialogOpen(true);
  };

  // 削除
  const handleConfirmedDelete = async () => {
    if (!fileUuid) return;
    try {
      setDeleting(true);
      const csrftoken = Cookies.get("csrftoken");
      const response = await fetch("/api/v1/text-file/" + fileUuid, {
        method: "DELETE",
        headers: { "X-CSRFToken": csrftoken! },
      });
      if (!response.ok) {
        setGlobalAlert("error", t("library.text.delete.error"));
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      await response.json();
      navigate("/notes-nebula/files");
      setDeleting(false);
      setGlobalAlert("success", t("library.text.delete.success"));
    } catch (error) {
      setDeleting(false);
      console.error("Error during the fetch operation:", error);
      setGlobalAlert("error", t("library.text.delete.error"));
    }
  };

  const actions = [
    { icon: saveIcon, name: t("textEditor.save"), action: handleSave, disabled: saving },
    { icon: <DownloadIcon />, name: t("common.download"), action: handleDownload },
    {
      icon: deleting ? <CircularProgress size={20} color="error" /> : <DeleteIcon color="error" />,
      name: t("common.delete"),
      action: handleDeleteDialogOpen,
      disabled: deleting,
    },
    {
      icon: <LaunchIcon />,
      name: t("textEditor.preview"),
      action: () => {
        let url = `/${languageValue}/notes-nebula/articles/${fileUuid}`;
        if (savedPublicStatus === 3 && currentPublicUuid) {
          url = `/${languageValue}/notes-nebula/articles/${fileUuid}/${currentPublicUuid}`;
        }
        window.open(url, "_blank");
      },
    },
    { icon: <FolderIcon />, name: t("textEditor.library"), action: () => navigate("/notes-nebula/files") },
  ];

  // フロートメニューの位置を設定
  useEffect(() => {
    const updateSpeedDialPosition = () => {
      const box = boxRef.current;
      if (box) {
        const rect = box.getBoundingClientRect();
        setSpeedDialStyle({
          position: "fixed",
          bottom: window.innerHeight - rect.bottom + 24,
          right: window.innerWidth - rect.right + 24,
        });
      }
    };

    const handleScrollEvent = () => {
      updateSpeedDialPosition();
    };

    const box = boxRef.current;
    if (box) {
      box.addEventListener("scroll", handleScrollEvent);
      const observer = new ResizeObserver(updateSpeedDialPosition);
      observer.observe(box);

      // 初回レンダリング
      updateSpeedDialPosition();

      // 遅延処理で確実に位置を更新（DOMが完全に描画された後）
      setTimeout(() => {
        updateSpeedDialPosition();
      }, 100);

      return () => {
        box.removeEventListener("scroll", handleScrollEvent);
        observer.disconnect();
      };
    }
  }, [boxRef]);

  return (
    <>
      <Box sx={{ transform: "translateZ(0px)", zIndex: 1300, ...speedDialStyle }}>
        {/* zIndex を追加 */}
        <SpeedDial ariaLabel="SpeedDial basic example" icon={<SpeedDialIcon />}>
          {actions.map((action) => (
            <SpeedDialAction
              key={action.name}
              icon={action.icon}
              tooltipTitle={action.name}
              onClick={action.action}
              FabProps={{
                disabled: action.disabled,
              }}
            />
          ))}
        </SpeedDial>
      </Box>
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{t("library.text.delete.title")}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t("library.text.delete.description")}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button disableElevation onClick={() => setDialogOpen(false)} color="primary">
            {t("common.cancel")}
          </Button>
          <Button
            color="error"
            onClick={() => {
              handleConfirmedDelete();
              setDialogOpen(false);
            }}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
export default React.memo(TextActionsComponent); // React.memoでラップ
